import React, { useCallback, useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { addAlertMessage } from 'redux/actions/alert-messages';
import {
  deleteUploadFile, generateAICourse, getAllUploadFiles, setGeneratingCourse,
  uploadFilesToAiCourseCreation,
} from 'redux/actions/ai-course-creation';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { CourseAliases } from 'redux/schemas/models/course';
import { getCourseAliases, getCurrentCourse } from 'redux/selectors/course';

import { S3NameSpaces } from 'shared/services/s3-upload-factory';
import useUploadedFile from 'shared/components/nv-uploaded-file/hooks/use-uploaded-file';
import useUploadFile from 'shared/hooks/use-upload-file';
import prodPathReplace from 'shared/prod-path-rewrite';

import NvFilePicker from 'shared/components/nv-filepicker';
import NvFlyoutModal, { ModalType } from 'shared/components/nv-flyout-modal';
import NvIcon from 'shared/components/nv-icon';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import NvUploadProgressBar from 'shared/components/nv-file-upload-progress-bar';
import NvTooltip from 'shared/components/nv-tooltip';

import { danger, gray4, hexToRgbaString, lightCyan, success } from 'styles/global_defaults/colors';
import {
  doubleSpacing, halfSpacing, largeSpacing, quarterSpacing,
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';

import { screenMdMin } from 'styles/global_defaults/media-queries';
import { AngularServicesContext } from 'react-app';
import uuid from 'react-uuid';
import BallAnimation from './ball-animation';
import { WelcomeMessages } from './utils';
import NovoAISearch from './search';
import { config } from '../../../../config/config.json';

const ALLOWED_FILE_TYPES = [
  ...config.files.aiCreationSourceUploadableFiles.documents.extensions,
  ...config.files.aiCreationSourceUploadableFiles.presentations.extensions,
];
const MAX_FILE_SIZE = config.files.aiCreationSourceAttachedFileSizeLimit * 1024 * 1024;

enum FileErrorCode {
  LARGE = 'file-too-large',
  SMALL = 'file-too-small',
  MANY_FILES = 'too-many-files',
  INVALID = 'file-invalid-type',
}

export interface FileError {
  message: string;
  code: FileErrorCode;
}

export interface FileRejection {
  file: File;
  errors: FileError[];
}

export interface FileRejectionWithId extends FileRejection {
  id: string;
}

enum FileStatus {
  UPLOADING = 'uploading',
  SUCCESS = 'success',
  FAILED = 'failed',
}

type TempFile = {
  name: string;
  size: number;
  type: string;
  status: FileStatus;
  id?: number; // Will be added after a successful upload
  url?: string;
  unique_id?: string; // Unique identifier for the file
};

type UploadResponse = {
  id: number;
  uniqueId: string;
  attachedFileName: string;
  attachedFileSize: number;
  attachedContentType: string;
  createdAt: string;
  updatedAt: string;
  url: string;
};

const styles = css`
  .flyout-content {
    overflow-y: auto;
    position: fixed;
    left: ${tripleSpacing}px !important;
    background: linear-gradient(315deg, rgba(220, 253, 253, 0.30) 0%, rgba(247, 251, 232, 0.30) 99.28%), #FFF;
    box-shadow: 0px 10px 10px 0px rgba(29, 33, 38, 0.10);

    @media (max-width: ${screenMdMin}px) {
      left: 0 !important;
      min-width: 100vw !important;
    }
  }

  .flyout-close-button {
    .close-button {
      position: fixed;
      right: ${largeSpacing}px;

      @media (min-width: ${screenMdMin}px) {
        left: 385px;
        right: 0;
      }
    }
  }

  .flyout-novo-ai-header-static {
    position: sticky;
    top: 0;
    z-index: 1000;
    background: linear-gradient(315.42deg, rgba(220, 253, 253, 0.3) 0%, rgba(247, 251, 232, 0.3) 99.28%);
    backdrop-filter: blur(10px);
  }
  .flyout-novo-ai-header {
    background: linear-gradient(315.42deg, rgba(220, 253, 253, 0.3) 0%, rgba(247, 251, 232, 0.3) 99.28%);
    gap: ${quarterSpacing}px;
  }

  .dropzone-box-active .upload-box {
    border-image-slice: 2;
    background-image: linear-gradient(${lightCyan}, ${lightCyan}), linear-gradient(48.41deg, #60f3ab, #00ccbc, #009ac0);
    background-clip: padding-box, border-box;
  }


  .upload-box  {
    cursor: pointer;
    position: relative;
    border-radius: 6px;
    border: 2px solid transparent;
    background-color: transparent; /* Transparent by default */
    transition: background-color 0.3s; /* Smooth transition for hover effect */
    z-index: 0; /* Ensure the .gradient-btn is on top */

    ::before {
      content: "";
      position: absolute;
      top: -1px;
      right: -1px;
      bottom: -1px;
      left: -1px;
      border-radius: inherit; /* Ensure the border radius is applied */
      padding: 2px; /* Adjust to match the border width */
      background: linear-gradient(48.41deg, #60F3AB 0%, #00CCBC 45%, #009AC0 100%);
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: destination-out;
      mask-composite: exclude; /* Make the center part transparent */
      z-index: -1;
    }

    &:hover {
      border-image-slice: 2;
      background-image: linear-gradient(${lightCyan}, ${lightCyan}), linear-gradient(48.41deg, #60f3ab, #00ccbc, #009ac0);
      background-clip: padding-box, border-box;
    }
  }

  .upload-box-txt1 {
    display: flex;
    gap: 3px;
    justify-content: center;
  }

  .upload-img-container {
    gap: ${quarterSpacing}px;
  }
  .small-star-img {
    transform: rotate(45deg);
    transform-origin: right;
    font-size : 7px;
  }
  .ball-Img {
    font-size: ${doubleSpacing}px;
  }
  .doc-box {
    cursor: pointer;
    &:focus-within,
    &:hover {
      background: ${hexToRgbaString('#00CCBC', 0.05)} !important;
      .trash-icon {
        opacity: 1; /* Make trash icon visible */
        pointer-events: auto; /* Enable clicking */
        position : relative;
        color : ${danger};
      }
      .success-icon  {
        opacity: 0 !important;
      }
      .trash {
        color : ${danger};
      }
    }

  }
  .file-img {
    width: 25px;
    height: 30px;
  }
  .file-name {
    width : 77% ;
  }

  .trash-icon {
    opacity: 0;
    // position: absolute;
    cursor: pointer;
    // pointer-events: none;
  }
  .success-icon{
    color: ${success};
    opacity: 1;
    position: absolute;
  }

  .disabled-trash {
    color: ${gray4};
    pointer-events: none; /* Prevent click by default */
    cursor: not-allowed;
  }

  .animated-success {
    animation: zoom-in-out 0.5s ease-in-out forwards;
  }
  @keyframes zoom-in-out {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  .bs4-progress {
    margin: ${halfSpacing}px ${standardSpacing}px 0px ${standardSpacing}px;
  }
  .bs4-progress-bar-animated {
    background-image: none;
  }
  .bs4-progress-bar {
    border-image-slice: 2;
    background: linear-gradient(48.41deg, #60F3AB 0%, #00CCBC 45%, #009AC0 100%);
    background-clip: padding-box, border-box;
  }
  .progress-bar1{
    height : 5px !important;
  }
  .underline-text {
    text-decoration: underline;
  }

  .text-ellipsis{
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .error-file-container {
    min-width: 80%
  }

  .upload-bar {
    width: 77%;
    margin: auto;
  }
`;

const getFileUniqueId = (file: File) => `${file.name}-${file.size}-${file.lastModified}`;

const FileRow = ({
  file,
  setSelectedFiles,
}) => {
  const dispatch = useAppDispatch();
  const currentCourse = useSelector(getCurrentCourse);
  const { getNormalizeFileType, getImageSrc } = useUploadedFile();
  const isFileUploading = file.status === FileStatus.UPLOADING;
  const { uploadFiles, abortUpload, isUploading, filesUploading, hasUploadPercentage } = useUploadFile();

  const uploadFile = useCallback(async (uploadingFile: File) => {
    try {
      const uploadedFiles = await uploadFiles([uploadingFile], S3NameSpaces.ATTACHMENTS);
      const { name, size, type, uniqueId } = uploadedFiles[0] || {};

      if (!uniqueId) {
        // Cancelled upload
        setSelectedFiles((prevFiles) => prevFiles.filter((f) => f.unique_id !== getFileUniqueId(uploadingFile)));
        return;
      }

      wrapThunkAction(
        dispatch(
          uploadFilesToAiCourseCreation({
            courseCatalogId: currentCourse.catalogId,
            fileName: name,
            fileSize: size,
            contentType: type,
            uniqueID: uniqueId,
          }),
        ),
      ).then((res) => {
        const responsePayload = res.payload as UploadResponse;
        const uploadedFile = {
          id: responsePayload.id,
          name: responsePayload.attachedFileName,
          size: responsePayload.attachedFileSize,
          type: responsePayload.attachedContentType,
          status: FileStatus.SUCCESS,
          url: responsePayload.url,
        };

        setSelectedFiles((prevFiles) => {
          const index = prevFiles.findIndex((f) => f.unique_id === getFileUniqueId(uploadingFile));
          if (index !== -1) {
            const updatedFiles = [...prevFiles];
            updatedFiles[index] = uploadedFile; // Replace temp file with uploaded file
            return updatedFiles;
          }
          return prevFiles;
        });
      }).catch(() => {
        dispatch(
          addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.OOPS(),
            message: t.FORM.ERROR_SOMETHING_WRONG(),
          }),
        );
        setSelectedFiles((fileList) => fileList.filter(f => f.status !== FileStatus.UPLOADING));
      });
    } catch (error) {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.OOPS(),
          message: t.FORM.ERROR_SOMETHING_WRONG(),
        }),
      );
      setSelectedFiles((fileList) => fileList.filter(f => f.status !== FileStatus.UPLOADING));
    }
  }, [currentCourse.catalogId, dispatch, setSelectedFiles, uploadFiles]);

  useEffect(() => {
    if (file.status === FileStatus.UPLOADING) {
      uploadFile(file.uploaded_file);
    }
  }, [file, uploadFile]);

  const deleteUploadFileFn = async (fileId: number) => {
    try {
      const response = await dispatch(deleteUploadFile({ catalogId: currentCourse.catalogId, id: fileId })).unwrap();
      if (response) {
        setSelectedFiles((prevFiles) => prevFiles.filter((f) => f.id !== fileId));
      }
    } catch (error) {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }
  };

  const handleDeleteFile = (fileId: number) => {
    dispatch(openConfirmationDialog({
      title: t.LECTURE_PAGES.LHS.NOVO_AI.DELETE_FILE(),
      bodyText: t.INSTITUTIONS.DASHBOARD.DELETION.ARE_YOU_SURE(),
      confirmText: t.INSTITUTIONS.DASHBOARD.DELETION.YES_SURE(),
      onConfirm: () => deleteUploadFileFn(fileId),
    }));
  };

  let uploadPercentage: number | false;
  if (hasUploadPercentage) {
    if (filesUploading?.[0]?.isUploading) {
      uploadPercentage = filesUploading?.[0]?.uploadPercentage ?? 0;
    } else if (filesUploading?.[0]?.isUploading === false) {
      // File got uploaded
      uploadPercentage = 100;
    }
  }

  return (
    <div
      className='mb-1 p-4 doc-box'
      tabIndex={0}
      role='button'
      aria-label={file.name}
    >
      <NvTooltip
        placement='top'
        text={file.name}
      >
        <React.Fragment>
          <div className='doc-list d-flex justify-content-between align-items-center'>
            <div className='pr-1 py-1 '>
              <img
                className='file-img'
                src={isFileUploading
                  ? prodPathReplace('images/file-other.png')
                  : getImageSrc(getNormalizeFileType(file.type, file.name), true)}
                alt={getNormalizeFileType(file.type, file.name)}
              />
            </div>
            <div className='file-name text-regular font-weight-bolder'>
              <div className='text-ellipsis'>{file.name}</div>
            </div>
            <div className='icon-container d-flex align-items-center justify-content-center position-relative'>
              {isFileUploading
                ? <NvIcon icon='trash' size='small' className='trash disabled-trash pl-2' onClick={abortUpload} data-qa={config.pendo.novoAiCourseCreation.icons.deleteIcon} />
                : (
                  <React.Fragment>
                    <NvIcon icon='success' size='small' className='success-icon animated-success pl-2' data-qa={config.pendo.novoAiCourseCreation.icons.successIcon} />
                    <NvIcon
                      icon='trash'
                      size='small'
                      className='trash-icon pl-2'
                      data-qa={config.pendo.novoAiCourseCreation.icons.deleteIcon}
                      onClick={() => handleDeleteFile(file.id)}
                      tabIndex={0}
                    />
                  </React.Fragment>
                )}
            </div>
          </div>
          {isFileUploading && (
            <div className='pt-2 upload-bar'>
              <NvUploadProgressBar isUploading={isFileUploading} inputProgress={uploadPercentage} />
            </div>
          )}
        </React.Fragment>
      </NvTooltip>
    </div>
  );
};

type FlyoutProps = {
  setShowFlyout: (show: boolean) => void;
};

const Flyout = ({
  setShowFlyout,
}: FlyoutProps) => {
  const [selectedFiles, setSelectedFiles] = useState<TempFile[]>([]);
  const [fileErrors, setFileErrors] = useState([]);
  const { selectedCourseId } = useSelector((state) => state.app.aiCourseCreation);
  const fileSizeTxt = '50MB';
  const aliases: CourseAliases = useSelector((state) => getCourseAliases(state));
  const dispatch = useAppDispatch();
  const currentCourse = useSelector(getCurrentCourse);
  const { TimelinesManager } = useContext(AngularServicesContext);
  const maxFilesCount = 10;

  useEffect(() => {
    const fetchUploadedFiles = async () => {
      try {
        const response = await dispatch(getAllUploadFiles({ catalogId: currentCourse.catalogId }));
        const uploadedFiles = response.payload as UploadResponse[];

        // Format response to match TempFile structure
        const formattedFiles = uploadedFiles.map((file) => ({
          id: file.id,
          name: file.attachedFileName,
          size: file.attachedFileSize,
          type: file.attachedContentType,
          status: FileStatus.SUCCESS, // Mark as uploaded
          url: file.url,
        }));

        setSelectedFiles(formattedFiles);
      } catch (error) {
        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.OOPS(),
          message: t.FORM.ERROR_SOMETHING_WRONG(),
        }));
      }
    };

    fetchUploadedFiles();
  }, [dispatch, currentCourse.catalogId]);

  const handleCloseFlyoutModal = () => {
    setShowFlyout(false);
  };

  const handleGenerate = (selectedLength) => {
    dispatch(setGeneratingCourse(true));
    setShowFlyout(false);
    wrapThunkAction(dispatch(generateAICourse({
      courseCatalogId: currentCourse.catalogId,
      contentLength: selectedLength,
      styleSourceCourseId: selectedCourseId,
    })))
      .then((response) => {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.LECTURE_PAGES.LHS.NOVO_AI.GENERATE.ALERT_SUCCESS_HEADER(),
          message: t.LECTURE_PAGES.LHS.NOVO_AI.GENERATE.ALERT_SUCCESS_MSG(aliases.courseAliases),
        }));

        if (TimelinesManager?.currentTimeline?.course) {
          if (TimelinesManager?.currentTimeline.course.aiCreationCommands) {
            TimelinesManager.currentTimeline.course.aiCreationCommands.push(response.payload);
          } else {
            TimelinesManager.currentTimeline.course.aiCreationCommands = [response.payload];
          }
        }
      })
      .catch(() => {
        dispatch(setGeneratingCourse(false));
        dispatch(addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.OOPS(),
          message: t.FORM.ERROR_SOMETHING_WRONG(),
        }));
      });
  };

  const handleFilePickerChange = async (files: File[]) => {
    if (selectedFiles.length + files.length > maxFilesCount) {
      dispatch(
        addAlertMessage({
          type: AlertMessageType.ERROR,
          header: t.FORM.OOPS(),
          message: t.LECTURE_PAGES.LHS.NOVO_AI.ERROR_MSG.FILES_LIMIT_ERROR(
            maxFilesCount,
          ),
        }),
      );
      return;
    }

    // Step 1: Show selected files with "uploading" status
    const tempFiles: TempFile[] = files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      status: FileStatus.UPLOADING,
      unique_id: getFileUniqueId(file),
      uploaded_file: file,
    }));

    setSelectedFiles((prevFiles) => [...prevFiles, ...tempFiles]);
  };

  const onFileError = (fileRejections: FileRejection[]) => {
    const newErrors = fileRejections.map(({ file, errors }) => {
      const errorMessage = errors.map((err) => {
        if (err.code === FileErrorCode.LARGE) {
          return `${t.LECTURE_PAGES.LHS.NOVO_AI.FILE_ERROR_LARGE()} `;
        } if (err.code === FileErrorCode.INVALID) {
          return `${t.LECTURE_PAGES.LHS.NOVO_AI.FILE_ERROR_INVALID()}`;
        }
        return `${t.LECTURE_PAGES.LHS.NOVO_AI.FILE_ERROR_DEFAULT()}`;
      });

      return { id: `${getFileUniqueId(file)}-${uuid()}`, file, message: errorMessage.join(' ') };
    });
    setFileErrors((prevErrors) => [...prevErrors, ...newErrors]);
  };

  const removeErrorFile = (fileId) => {
    setFileErrors((prevErrors) => prevErrors.filter((error) => error.id !== fileId));
  };

  return (
    <div css={styles}>
      <NvFlyoutModal
        type={ModalType.LEFT}
        width={375}
        onClose={handleCloseFlyoutModal}
        closeDataQa={config.pendo.novoAiCourseCreation.icons.closeIcon}
      >
        <div>
          <div className='flyout-novo-ai-header-static'>
            <div className='flyout-novo-ai-header  py-3 px-4 d-flex justify-content-center align-items-center position-sticky'>
              <GradientAiIcon icon='gen-ai' size='medium' />
              <span className='flyout-novo-ai-text text-black bold text-large-body '>{t.LECTURE_PAGES.LHS.NOVO_AI.START_BUTTON()}</span>
            </div>
          </div>
          <div className='p-4'>
            <WelcomeMessages />
            <NvFilePicker
              accept={ALLOWED_FILE_TYPES}
              onChange={handleFilePickerChange}
              multiple
              maxSize={MAX_FILE_SIZE}
              noDrag={false}
              onSelectError={(e) => {
                onFileError(e as FileRejection[]);
              }}
            >
              <div
                className='upload-box text-align-center my-4 px-4 py-4'
                tabIndex={0}
                role='button'
              >
                <div className='upload-box-txt1 text-body font-weight-bolder' data-qa={config.pendo.novoAiCourseCreation.dragAndDropText}>
                  <span>{t.LECTURE_PAGES.LHS.NOVO_AI.UPLOAD_TEXT_ONE.DARG_AND_DROP()}</span>
                  <span className='underline-text'>{t.LECTURE_PAGES.LHS.NOVO_AI.UPLOAD_TEXT_ONE.BROWSE_TEXT()}</span>
                  <span>{t.LECTURE_PAGES.LHS.NOVO_AI.UPLOAD_TEXT_ONE.FILES()}</span>
                </div>
                <div className='upload-img-container d-flex align-items-center justify-content-center p-2 my-2'>
                  <BallAnimation />
                </div>
                <div className='upload-box-txt2 text-gray-2 text-small'>{t.LECTURE_PAGES.LHS.NOVO_AI.UPLOAD_TEXT_TWO(fileSizeTxt)}</div>
              </div>
            </NvFilePicker>
            {
              selectedFiles.length > 0
              && (
                <div>
                  <div className='mt-4 mb-2 text-gray-2 font-weight-bolder' data-qa={config.pendo.novoAiCourseCreation.uploadFiles}>{t.LECTURE_PAGES.LHS.NOVO_AI.UPLOAD_FILES()}</div>
                  {selectedFiles.map((file) => (
                    <FileRow key={`${file.unique_id ?? file.id}`} file={file} setSelectedFiles={setSelectedFiles} />
                  ))}
                </div>
              )
            }
            {fileErrors.length > 0 && (
              fileErrors.map(error => (
                <div className='doc-list d-flex p-4 justify-content-between align-items-center'>
                  <NvIcon icon='warning' size='small' className='error-icon text-danger mr-2' data-qa={config.pendo.novoAiCourseCreation.icons.warningIcon} />
                  <div className='error-file-container d-flex flex-column flex-grow-1'>
                    <div className='text-ellipsis text-regular'>{error.file.name}</div>
                    <div className='text-regular'>{error.message}</div>
                  </div>
                  <NvIcon
                    icon='close'
                    size='small'
                    className='close pl-2'
                    data-qa={config.pendo.novoAiCourseCreation.icons.closeIcon}
                    onClick={() => removeErrorFile(error.id)}
                  />
                </div>
              ))
            )}
            {selectedFiles.length > 0 && selectedFiles[0]?.status === FileStatus.SUCCESS && (
              <NovoAISearch handleGenerate={handleGenerate} />
            )}
          </div>
        </div>
      </NvFlyoutModal>
    </div>
  );
};

export default Flyout;
